import React, { useMemo } from 'react';
import { ContentParser } from '@/components/universal/ContentParser';
import { formatContentForDisplay } from '@/utils/contentParserV2';
import { usePersistentContent } from '@/hooks/usePersistentContent';
import { editableProps, useIsVisualEditor } from '@/components/inline/directus-ve';
import { Loader2 } from 'lucide-react';

type UniversalContentEditorProps<T extends React.ElementType> = {
  collection: string;
  itemId: string | number;
  field: string;
  value?: string;
  placeholder?: string;
  tag?: T;
  /** @deprecated kept for API compatibility — editing now happens in the Directus drawer */
  showEditIcon?: boolean;
  /** @deprecated kept for API compatibility — editing now happens in the Directus drawer */
  alwaysEditing?: boolean;
  /** @deprecated kept for API compatibility — saving now happens in the Directus drawer */
  onContentChange?: (content: string) => void;
} & Omit<React.ComponentPropsWithoutRef<T>, 'value' | 'onChange'>;

/**
 * Universal content renderer backed by the Directus 12 Visual Editor.
 *
 * It always renders the live field value (read-only). When — and only when —
 * the page is loaded inside the Directus Studio Visual Editor, it marks the
 * element with a `data-directus` attribute so the Studio can overlay it and
 * open its edit drawer. There is no in-app editor: outside the Directus UI the
 * site is strictly read-only, even for authenticated administrators.
 */
export const UniversalContentEditor = <T extends React.ElementType = 'div'>({
  collection,
  itemId,
  field,
  value = '',
  placeholder = '',
  tag,
  // Deprecated props are accepted but no longer used (editing is in the drawer).
  showEditIcon: _showEditIcon,
  alwaysEditing: _alwaysEditing,
  onContentChange: _onContentChange,
  ...rest
}: UniversalContentEditorProps<T>) => {
  const Tag = (tag || 'div') as React.ElementType;

  // Editing affordances are emitted only inside the Directus Studio. Reactive,
  // so the marker appears as soon as the Visual Editor connection is confirmed.
  const editing = useIsVisualEditor();

  // Load the live field value for display (the `value` prop is the fallback).
  const { content, isLoading } = usePersistentContent({
    collection,
    itemId,
    field,
    initialValue: value,
  });

  // Fall back to the provided default when the live value is empty or failed to
  // load (an empty string is falsy, so `||` — not `??` — is what we want here).
  const parsedContent = useMemo(
    () => formatContentForDisplay(content || value || ''),
    [content, value],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-2">
        <Loader2 size={16} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Tag {...rest} {...editableProps(editing, { collection, item: itemId, fields: field })}>
      {parsedContent ? (
        <ContentParser content={parsedContent} />
      ) : placeholder ? (
        <span className="text-gray-400">{placeholder}</span>
      ) : null}
    </Tag>
  );
};

export default UniversalContentEditor;
